import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell as faBellSolid, faTimes } from '@awesome.me/kit-269fbef2e2/icons/duotone/solid';
import { faBell as faBellOutline } from '@awesome.me/kit-269fbef2e2/icons/duotone/solid';
import { faCheck } from '@awesome.me/kit-269fbef2e2/icons/duotone/solid';
import { notificationService } from '@/services/notificationService';
import { useTenancy } from '@/contexts/TenancyContext';
import { useAuth } from '@/contexts/AuthContext';
import { NotificationSystem } from '@/js/external-js/variableModules/namespaces/NotificationSystem/NotificationSystem.namespace';
import { cleanDateAndTimeObject, toLocalDateString } from '@shared/renderDate';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, Target, VariantLabels, animationControls, TargetAndTransition, Transition, Variants, BoundingBox, PanInfo, Box, AnimationDefinition, EventInfo } from 'framer-motion';

declare module 'react' {
  interface HTMLAttributes<T> extends AriaAttributes, DOMAttributes<T> {
    // Allow data-* attributes
    [key: `data-${string}`]: any;
    // Add framer-motion props
    layout?: boolean | 'position' | 'size' | 'preserve-aspect' | 'crossfade' | undefined;
    layoutId?: string;
    layoutDependency?: any;
    layoutScroll?: boolean;
    layoutRoot?: boolean;
    initial?: boolean | Target | VariantLabels | undefined;
    animate?: typeof animationControls | TargetAndTransition | VariantLabels | boolean | undefined;
    exit?: TargetAndTransition | VariantLabels | undefined;
    transition?: Transition | undefined;
    variants?: Variants | undefined;
    whileHover?: VariantLabels | TargetAndTransition | undefined;
    whileTap?: VariantLabels | TargetAndTransition | undefined;
    whileFocus?: VariantLabels | TargetAndTransition | undefined;
    whileDrag?: VariantLabels | TargetAndTransition | undefined;
    drag?: boolean | 'x' | 'y' | undefined;
    dragConstraints?: false | Partial<BoundingBox> | RefObject<Element> | undefined;
    dragElastic?: number | boolean | { top?: number; left?: number; right?: number; bottom?: number } | undefined;
    dragMomentum?: boolean | undefined;
    dragPropagation?: boolean | undefined;
    dragTransition?: { bounceStiffness?: number; bounceDamping?: number; min?: number; max?: number } | undefined;
    onDragStart?: (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => void;
    onDragEnd?: (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => void;
    onViewportBoxUpdate?: (box: Box, delta: any) => void;
    onAnimationStart?: (definition: AnimationDefinition) => void;
    onAnimationComplete?: (definition: AnimationDefinition) => void;
    onUpdate?: (latest: { [key: string]: string | number }) => void;
    onHoverStart?: (event: MouseEvent, info: EventInfo) => void;
    onHoverEnd?: (event: MouseEvent, info: EventInfo) => void;
    onTap?: (event: MouseEvent | TouchEvent | PointerEvent, info: EventInfo) => void;
    onTapStart?: (event: MouseEvent | TouchEvent | PointerEvent, info: EventInfo) => void;
    onTapCancel?: (event: MouseEvent | TouchEvent | PointerEvent, info: EventInfo) => void;
    onDirectionLock?: (axis: 'x' | 'y') => void;
    onDragTransitionEnd?: () => void;
    onMeasureDragConstraints?: (constraints: BoundingBox) => BoundingBox | void;
    onPan?: (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => void;
    onPanStart?: (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => void;
    onPanEnd?: (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => void;
    onPanSessionStart?: (event: MouseEvent | TouchEvent | PointerEvent, info: EventInfo) => void;
    onPanSessionEnd?: (event: MouseEvent | TouchEvent | PointerEvent, info: EventInfo) => void;
    onLayoutAnimationComplete?: () => void;
    onLayoutMeasure?: (dimensions: BoundingBox) => void;
  }
}

// Use the NotificationSystem interface directly
type Notification = NotificationSystem.Notification;

export function NotificationCenter() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const { activeTenancy } = useTenancy();
  const { user } = useAuth();

  // Initialize notification service and set up real-time listener
  useEffect(() => {
    if (!activeTenancy?.id || !user?.uid) return;

    // Initialize the notification service
    notificationService.setCustomerID(activeTenancy.id);
    notificationService.setCurrentUserID(user.uid);

    // Set up real-time listener for notifications
    const handleNotificationUpdate = (updatedNotifications: Notification[]) => {

      // Remove deleted notifications
      const filtered = updatedNotifications.filter(n => !n.deletedAt);
      
      // DED-1443: Sort notifications by timestamp (newest first) using cleanDateAndTimeObject
      const sorted = [...filtered].sort((a, b) => {
        const timeA = a.timestamp ? cleanDateAndTimeObject(a.timestamp).getTime() : 0;
        const timeB = b.timestamp ? cleanDateAndTimeObject(b.timestamp).getTime() : 0;
        return timeB - timeA; // Sort in descending order (newest first)
      });

      
      setNotifications(sorted);
      const unread = sorted.filter(n => !n.read).length;
      setUnreadCount(unread);
    };

    // Listen for real-time updates
    notificationService.listenForNotifications(handleNotificationUpdate);

    // Also fetch initial notifications
    const fetchInitialNotifications = async () => {
      try {
        const initialNotifications = await notificationService.getNotifications(20);
        // Remove deleted notifications
        const filtered = initialNotifications.filter(n => !n.deletedAt);
        // Sort initial notifications by timestamp (newest first)
        const sorted = [...filtered].sort((a, b) => {
          const timeA = a.timestamp ? cleanDateAndTimeObject(a.timestamp).getTime() : 0;
          const timeB = b.timestamp ? cleanDateAndTimeObject(b.timestamp).getTime() : 0;
          return timeB - timeA; // Sort in descending order (newest first)
        });
        
        setNotifications(sorted);
        setUnreadCount(sorted.filter(n => !n.read).length);
      } catch (error) {
        console.error('Error fetching initial notifications:', error);
      }
    };

    fetchInitialNotifications();
  }, [activeTenancy?.id, user?.uid]);

  const markAsRead = async (id: string) => {
    try {
      await notificationService.markAsRead(id, (updatedNotifications) => {
        // Remove deleted notifications
        const filtered = updatedNotifications.filter(n => !n.deletedAt);
        // DED-1443: Sort notifications by timestamp (newest first) using cleanDateAndTimeObject
        const sorted = [...filtered].sort((a, b) => {
          const timeA = a.timestamp ? cleanDateAndTimeObject(a.timestamp).getTime() : 0;
          const timeB = b.timestamp ? cleanDateAndTimeObject(b.timestamp).getTime() : 0;
          return timeB - timeA; // Sort in descending order (newest first)
        });
        setNotifications(sorted);
        setUnreadCount(sorted.filter(n => !n.read).length);
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const clearNotification = async (id: string) => {
    try {
      console.log('Clearing notification:', id);
      await notificationService.clearNotification(id);
      // Remove deleted notifications
      const filtered = notifications.filter(n => !n.deletedAt);
      // DED-1443: Sort notifications by timestamp (newest first) using cleanDateAndTimeObject
      const sorted = [...filtered].sort((a, b) => {
        const timeA = a.timestamp ? cleanDateAndTimeObject(a.timestamp).getTime() : 0;
        const timeB = b.timestamp ? cleanDateAndTimeObject(b.timestamp).getTime() : 0;
        return timeB - timeA; // Sort in descending order (newest first)
      });
      setNotifications(sorted);
      setUnreadCount(sorted.filter(n => !n.read).length);
    } catch (error) {
      console.error('Error clearing notification:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead((updatedNotifications) => {
        // Remove deleted notifications
        const filtered = updatedNotifications.filter(n => !n.deletedAt);
        // DED-1443: Sort notifications by timestamp (newest first) using cleanDateAndTimeObject
        const sorted = [...filtered].sort((a, b) => {
          const timeA = a.timestamp ? cleanDateAndTimeObject(a.timestamp).getTime() : 0;
          const timeB = b.timestamp ? cleanDateAndTimeObject(b.timestamp).getTime() : 0;
          return timeB - timeA; // Sort in descending order (newest first)
        });
        setNotifications(sorted);
        setUnreadCount(sorted.filter(n => !n.read).length);
      });
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const formatTimeAgo = (timestamp: unknown): string => {
    if (!timestamp) return 'Just now';

    let date: Date;
    try {
      // First try the cleanDateAndTimeObject utility
      const cleanedDate = cleanDateAndTimeObject(timestamp);
      if (cleanedDate && !isNaN(cleanedDate.getTime())) {
        date = cleanedDate;
      } else {
        // Fallback to manual parsing if cleanDateAndTimeObject fails
        if (timestamp instanceof Date) {
          date = timestamp;
        } else if (typeof timestamp === 'string' || typeof timestamp === 'number') {
          date = new Date(timestamp);
        } else if (timestamp && typeof timestamp === 'object' && 'toDate' in timestamp && typeof timestamp.toDate === 'function') {
          // Handle Firebase Timestamp objects
          date = timestamp.toDate();
        } else if (timestamp && typeof timestamp === 'object' && 'seconds' in timestamp && typeof timestamp.seconds === 'number') {
          // Handle Firebase Timestamp in {seconds: number, nanoseconds?: number} format
          date = new Date(timestamp.seconds * 1000);
        } else if (timestamp && typeof timestamp === 'object' && '_seconds' in timestamp && typeof (timestamp as any)._seconds === 'number') {
          // Handle Firestore Timestamp with _seconds property
          date = new Date((timestamp as any)._seconds * 1000);
        } else {
          console.warn('Unrecognized timestamp format:', timestamp);
          return 'Just now';
        }
      }

      // Final validation
      if (isNaN(date.getTime())) {
        console.warn('Invalid date from timestamp:', timestamp);
        return 'Just now';
      }

      const now = new Date();
      const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

      if (diffInSeconds < 60) return 'Just now';
      if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
      if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
      // DED-1443: Use toLocalDateString for proper locale-aware date formatting
      return toLocalDateString(date) || date.toLocaleDateString();
    } catch (error) {
      console.error('Error formatting timestamp:', error, 'Original timestamp:', timestamp);
      return 'Just now';
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            'relative rounded-full h-9 w-9 flex items-center justify-center',
            'transition-all duration-200 hover:bg-accent/50',
            isOpen ? 'bg-accent/30' : 'bg-transparent'
          )}
          onClick={() => setIsOpen(!isOpen)}
        >
          <FontAwesomeIcon
            icon={unreadCount > 0 ? faBellSolid : faBellOutline}
            className="h-4 w-4 text-foreground/80"
          />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-medium text-white">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
          <span className="sr-only">Notifications</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn(
          'w-80 p-0 border-0 overflow-hidden',
          'bg-white/60 dark:bg-secondary/60 backdrop-blur-lg', // Glass effect
          'shadow-lg ring-1 ring-border/20', // Subtle border
          'transition-all duration-200 ease-in-out',
          'before:absolute before:inset-0 before:bg-white/10 before:backdrop-blur-xl before:-z-10' // Frosted glass overlay
        )}
        align="end"
        sideOffset={8}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border/20">
          <h3 className="font-medium text-foreground">Notifications</h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs text-muted-foreground bg-accent/10 hover:bg-accent/20 hover:text-foreground"
              onClick={(e) => {
                e.stopPropagation();
                markAllAsRead();
              }}
            >
              Mark all as read
            </Button>
          )}
        </div>
        <div className="max-h-[400px] flex flex-col">
          {notifications.length === 0 ? (
            <motion.div className="p-6 text-center">
              <motion.div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-muted/50 mb-3">
                <FontAwesomeIcon icon={faBellSolid} className="h-5 w-5 text-muted-foreground" />
              </motion.div>
              <p className="text-sm text-muted-foreground">No new notifications</p>
            </motion.div>
          ) : (
            <>
              <motion.div
                layout
                className="divide-y divide-border/20 overflow-y-auto flex-1"
                initial={false}
                transition={{
                  duration: 0.1,
                  ease: 'easeInOut',
                  opacity: { duration: 0.1 }
                }}
              >
                {notifications
                  .slice(0, showAll ? notifications.length : 3)
                  .map((notification) => (
                    <motion.div
                      layout
                      key={notification.id}
                      className={cn(
                        'p-3 transition-colors group relative',
                        'hover:bg-accent/30',
                        !notification.read && 'border-l-2 border-b-0 border-primary bg-accent/5',
                      )}
                      onClick={notification.link ? () => {
                        markAsRead(notification.id);
                        
                        // Check if this is an AI suggestion link and redirect to system-update
                        if (notification.link.includes('/AI/assurance/suggestions/')) {
                          navigate('/system-update');
                          return;
                        }
                        
                        // Original logic for other link types
                        if (notification.link.includes('http') || notification.link.includes('.html') || notification.link.includes('mailto')) {
                          window.open(notification.link, '_blank');
                        } else {
                          navigate(notification.link);
                        }
                      } : () => markAsRead(notification.id)}
                    >
                      <motion.div className="flex items-start gap-3">
                        <motion.div className={cn(
                          'mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full',
                          'bg-primary/10 text-primary'
                        )}>
                          <FontAwesomeIcon icon={faBellSolid} className="h-2.5 w-2.5" />
                        </motion.div>
                        <motion.div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-foreground line-clamp-1">
                            {notification.title}
                          </h4>
                          <p className="mt-0.5 text-sm text-muted-foreground line-clamp-2">
                            {notification.body}
                          </p>
                          <motion.div className="mt-1.5 flex items-center justify-between">
                            <span className="text-xs text-muted-foreground">
                              {formatTimeAgo(notification.timestamp)}
                            </span>
                            {!notification.read && (
                              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                            )}
                          </motion.div>
                        </motion.div>
                        <button
                          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 -mr-1.5"
                          onClick={(e) => {
                            e.stopPropagation();
                            clearNotification(notification.id);
                            // markAsRead(notification.id);
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faTimes}
                            className="h-3 w-3 text-muted-foreground hover:text-foreground"
                          />
                        </button>
                      </motion.div>
                    </motion.div>
                  ))}
              </motion.div>
            </>
          )}
        </div>
        <AnimatePresence>
            {notifications.length > 3 && (
              <motion.div
                initial={{ opacity: 0, y: 10, height: 0 }}
                animate={{ opacity: 1, y: 0, height: 'auto' }}
                exit={{ opacity: 0, y: 10, height: 0 }}
                transition={{
                  type: 'spring',
                  stiffness: 300,
                  damping: 25,
                  opacity: { duration: 0.15 }
                }}
                className="sticky bottom-0 overflow-hidden bg-background/80 backdrop-blur-sm border-t border-border/20"
              >
                <div className="p-2">
                  <motion.button
                    className="text-xs w-full py-1.5 px-3 rounded-md text-muted-foreground bg-accent/10 hover:bg-accent/20 hover:text-foreground transition-all"
                    onClick={() => setShowAll(prev => !prev)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <motion.span
                      key={showAll ? 'less' : 'more'}
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -5 }}
                      transition={{ duration: 0.2 }}
                    >
                      {showAll ? 'Show Less' : `Show All Notifications (${notifications.length})`}
                    </motion.span>
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
      </PopoverContent>
    </Popover>
  );
}
